<script setup lang="ts">
import { useSerialize } from "@lxc/app-device-common";
import type LxcSideCanvas from "@lxc/app-device-common/src/components/LxcSideCanvas.vue";
import LxcForm from "@lxc/app-device-common/src/components/form/LxcForm.vue";
import type { Rules } from "async-validator";
import Schema from "async-validator";
import type { InternalRuleItem } from "async-validator/dist-types/interface";
import type { ComputedRef, ModelRef } from "vue";
import { useAcl } from "vue-simple-acl";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { useCreatePki } from "~/modules/pki/composables/useCreatePki";
import { useFetchPki } from "~/modules/pki/composables/useFetchPki";
import { useFetchPkiCertificateAuthorities } from "~/modules/pki/composables/useFetchPkiCertificateAuthorities";
import { useFetchPkiConnectivity } from "~/modules/pki/composables/useFetchPkiConnectivity";
import { useUpdatePki } from "~/modules/pki/composables/useUpdatePki";
import type { Pki } from "~/modules/pki/models/Pki.interface";
import { PkiType } from "~/modules/pki/models/PkiType.enum";
import ILxcActivity from "~icons/lxc/activity";
import ILxcHelpCircle from "~icons/lxc/help-circle";
import ILxcInfo from "~icons/lxc/info";

const props = defineProps<{
  selectedPki?: string;
}>();

const show: ModelRef<boolean> = defineModel<boolean>("show", {
  required: true,
});

const emits = defineEmits(["close"]);
const edited: Ref<boolean> = ref(false);

const { t } = useI18n();
const serialize = useSerialize();
const { createPki, isLoading, error } = useCreatePki();
const {
  isLoading: isLoadingCertificateAuthorities,
  results: certificateAuthoritiesResults,
  fetchPkiCertificateAuthorities,
} = useFetchPkiCertificateAuthorities();
const {
  isLoading: isLoadingPki,
  error: errorFetchPki,
  result: pki,
  fetchPki,
} = useFetchPki();
const {
  updatePki,
  isLoading: isLoadingUpdate,
  error: updateError,
} = useUpdatePki();
const {
  fetchConnectivity,
  fetchConnectivityWithInfos,
  isLoading: isLoadingPkiConnectivity,
  success: successPkiConnectivity,
  fail: errorPkiConnectivity,
  resetState: resetPkiConnectivityState,
} = useFetchPkiConnectivity();
const { can } = useAcl();

const pkiFormRef: Ref<typeof LxcForm | undefined> = ref();
const sideCanvasPkiFormRef: Ref<typeof LxcSideCanvas | undefined> = ref();

function initializePkiForm(): Pki {
  return {
    alias: "",
    url: "",
    type: PkiType.EST,
    trustChainsAliases: [],
    authentication: {
      username: "",
      password: "",
    },
    specificConfiguration: {
      templateName: undefined,
      retryAfter: undefined,
      urlLabel: undefined,
    },
  };
}

const pkiForm: Pki = reactive(initializePkiForm());

const initPkiFormStringified: Ref<string> = ref(serialize(pkiForm));
const formHasChanged: ComputedRef<boolean> = computed(
  () => serialize(pkiForm) !== initPkiFormStringified.value,
);
const isPkiEdition = computed(() => !!props.selectedPki);

const initPkiFormTestStringified: Ref<string> = ref(serializeTestPkiForm());
const formTestHasChanged: ComputedRef<boolean> = computed(
  () => serializeTestPkiForm() !== initPkiFormTestStringified.value,
);
const pkiTestConnectionTitle: ComputedRef<string> = computed(() => {
  // no modification
  let buttonTitle = t("pki.form.testConnection.button");
  if (formTestHasChanged.value && !pkiForm.authentication?.password) {
    buttonTitle = t("pki.form.testConnection.noPassword");
  }
  return buttonTitle;
});

const isUrlLabelValid = (
  _: InternalRuleItem,
  value: string,
  callback: (error?: Error) => void,
) => {
  if (value !== undefined && value.length === 0) {
    return callback();
  }
  const validator = /^[a-zA-Z0-9_-]+$/.test(value);
  if (!validator) {
    return callback(new Error(t("pki.form.validation.urlLabel.format")));
  }
  return callback();
};

const formRules: ComputedRef<Rules> = computed(() => {
  const rules: Rules = {
    alias: [
      {
        required: true,
        message: t("pki.form.validation.alias.required"),
      },
      {
        type: "string",
        max: 50,
        message: t("pki.form.validation.alias.max"),
      },
    ],
    url: [
      {
        required: true,
        message: t("pki.form.validation.url.required"),
      },
      {
        type: "string",
        max: 120,
        message: t("pki.form.validation.url.max"),
      },
    ],
    type: [
      {
        required: true,
        message: t("pki.form.validation.type.required"),
      },
    ],
    "authentication.username": [
      {
        required: true,
        message: t("pki.form.validation.login.required"),
      },
    ],
    "authentication.password": [
      {
        required: true,
        message: t("pki.form.validation.password.required"),
      },
    ],
  };

  if (pkiForm.type === PkiType.MICROSOFT) {
    rules["specificConfiguration.templateName"] = [
      {
        required: true,
        message: t("pki.form.validation.templateName.required"),
      },
    ];
    rules["specificConfiguration.retryAfter"] = [
      {
        required: true,
        message: t("pki.form.validation.intervalTime.required"),
      },
      {
        type: "number",
        min: 60,
        message: t("pki.form.validation.intervalTime.min"),
      },
    ];
  } else if (pkiForm.type === PkiType.EST) {
    rules["specificConfiguration.urlLabel"] = [
      {
        type: "string",
        max: 100,
        message: t("pki.form.validation.urlLabel.max"),
      },
      { validator: isUrlLabelValid },
    ];
  }

  return rules;
});

const isFormValid: ComputedRef<boolean | undefined> = computed(
  () => pkiFormRef.value?.isValid,
);

const isFormTestValid: Ref<boolean | undefined> = computedAsync<
  boolean | undefined
>(async () => {
  let result = true;
  const descriptor: Rules = {
    url: formRules.value.url,
    username: formRules.value["authentication.username"],
    password: formRules.value["authentication.password"],
  };
  const validator = new Schema(descriptor);
  try {
    await validator.validate(getTestPkiForm());
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    result = false;
  }
  return result;
});

const isTestButtonDisabled: ComputedRef<boolean> = computed(() => {
  return (
    (formTestHasChanged.value || !props.selectedPki) && !isFormTestValid.value
  );
});

const certificateAuthorities = computed(() => {
  return certificateAuthoritiesResults.value.map((certificateAuthority) => ({
    ...certificateAuthority,
    available:
      certificateAuthority.available ||
      (props.selectedPki &&
        pki.value?.trustChainsAliases?.includes(certificateAuthority.alias)),
  }));
});

const isReadOnly = computed(() => !can(ACLRoles.CAN_WRITE_FEATURE));

function serializeTestPkiForm() {
  return serialize(getTestPkiForm());
}

function getTestPkiForm() {
  return {
    url: pkiForm.url,
    trustChainsAliases: pkiForm.trustChainsAliases,
    username: pkiForm.authentication?.username,
    password: pkiForm.authentication?.password || "",
  };
}

async function onSubmit() {
  if (isFormValid.value) {
    if (isPkiEdition.value) {
      await updatePki(props.selectedPki!, pkiForm);

      if (updateError.value) {
        updateError.value.notify(NotificationKey.saveError);
      } else {
        onSuccess();
      }
    } else {
      await createPki(pkiForm);

      if (error.value) {
        error.value.notify(NotificationKey.saveError);
      } else {
        onSuccess();
      }
    }
  }
}

function onSuccess() {
  showNotificationSuccess(t(NotificationKey.saveSuccess));
  edited.value = true;
  onClose();
}

function onClose() {
  clearForm();
  emits("close", { edited: edited.value });
  edited.value = false;
}

function onCancelClick() {
  sideCanvasPkiFormRef.value?.hide();
}

function clearForm() {
  Object.assign(pkiForm, initializePkiForm());
  initPkiFormStringified.value = serialize(pkiForm);
  resetPkiConnectivityState();
  setTimeout(() => {
    pkiFormRef.value?.clearValidate();
  }, 0);
}

const pkiTypes: ComputedRef<PkiType[]> = computed(() => Object.values(PkiType));

async function getPki() {
  if (props.selectedPki) {
    await fetchPki(props.selectedPki);

    if (!errorFetchPki.value && pki.value) {
      pkiForm.alias = pki.value.alias;
      pkiForm.url = pki.value.url;
      pkiForm.type = pki.value.type;
      pkiForm.trustChainsAliases = pki.value.trustChainsAliases;
      pkiForm.authentication = {
        username: pki.value.authentication.username,
      };
      pkiForm.specificConfiguration = {
        templateName: pki.value?.specificConfiguration?.templateName,
        retryAfter: pki.value?.specificConfiguration?.retryAfter,
        urlLabel: pki.value?.specificConfiguration?.urlLabel,
      };
      initPkiFormStringified.value = serialize(pkiForm);
      initPkiFormTestStringified.value = serializeTestPkiForm();
    }
  }
}

function testConnection() {
  if (formTestHasChanged.value && pkiForm.authentication?.password) {
    fetchConnectivityWithInfos({
      url: pkiForm.url,
      type: pkiForm.type,
      authentication: {
        username: pkiForm.authentication.username,
        password: pkiForm.authentication.password,
      },
    });
  } else if (!formTestHasChanged.value) {
    fetchConnectivity(pkiForm.alias);
  }
}

const getRadioWrapperClass = (pkiType?: PkiType) => {
  const baseClass = [
    "flex",
    "items-center",
    "rounded-xl",
    "p-4",
    "border",
    "min-w-36",
  ];

  if (pkiType === pkiForm.type) {
    return [...baseClass, "border-primary-700", "bg-primary-50"];
  } else {
    return [...baseClass, "border-gray-300", "bg-white"];
  }
};

watch(
  () => [props.selectedPki, show.value],
  async ([selectedPki, show]) => {
    if (show) {
      isLoadingPki.value = true;

      await fetchPkiCertificateAuthorities();
      if (selectedPki) {
        await getPki();
      }

      isLoadingPki.value = false;
    }
  },
  { immediate: true },
);

const containerKey = ref(0);
watch(
  () => pkiForm.type,
  (newValue) => {
    if (newValue) {
      // Force the re-render by changing the container component key to display
      // the required mention (asterisk) on the fields for microsoft pki type configuration
      containerKey.value++;
    }
  },
);
</script>

<template>
  <lxc-side-canvas
    ref="sideCanvasPkiFormRef"
    v-model:show="show"
    :header="
      isPkiEdition ? t('pki.form.title.update') : t('pki.form.title.create')
    "
    size="1/2"
    :confirm-title="
      isPkiEdition
        ? t('pki.form.cancel.confirm.title.update')
        : t('pki.form.cancel.confirm.title.create')
    "
    :confirm-message="
      isPkiEdition
        ? t('pki.form.cancel.confirm.message.update')
        : t('pki.form.cancel.confirm.message.create')
    "
    :confirm-ok-label="t('button.confirm')"
    :confirm-cancel-label="t('button.cancel')"
    :confirm-enabled="formHasChanged"
    @hidden="onClose"
  >
    <container-component
      :key="containerKey"
      :px="0"
      :py="0"
      :error="errorFetchPki"
      :is-loading="isLoadingPki"
    >
      <lxc-information class="!px-0 !py-0">
        <template #component>
          <lxc-info-sign>
            <i-lxc-info
              width="1.25rem"
              height="1.25rem"
              view-box="0 0 1.25rem 1.25rem"
            />
          </lxc-info-sign>
        </template>
        <template #body>
          <h6>{{ t("pki.form.warning.title") }}</h6>
          <span>{{ t("pki.form.warning.message") }}</span>
        </template>
      </lxc-information>
      <h6 class="mt-6 text-gray-600">
        {{ t("pki.form.connectionSettings.title") }}
      </h6>
      <lxc-form
        ref="pkiFormRef"
        :model="pkiForm"
        :rules="formRules"
        class="grid grid-cols-2 gap-x-5 gap-y-3 mt-4"
      >
        <lxc-form-item :label="t('pki.alias')" prop="alias">
          <lxc-input
            v-model="pkiForm.alias"
            type="text"
            :disabled="isPkiEdition"
          />
        </lxc-form-item>
        <lxc-form-item :label="t('pki.url')" prop="url" class="col-span-2">
          <lxc-input v-model="pkiForm.url" type="url" :disabled="isReadOnly" />
        </lxc-form-item>
        <lxc-form-item
          :label="t('pki.form.trustChainsAliases.label')"
          prop="trustChainsAliases"
        >
          <lxc-select
            v-model="pkiForm.trustChainsAliases"
            multiple
            select-filter
            :select-all-label="t('pki.form.trustChainsAliases.selectAll')"
            :select-search-placeholder="
              t('pki.form.trustChainsAliases.selectSearchPlaceholder')
            "
            :select-no-result-text="
              t('pki.form.trustChainsAliases.selectNoResult')
            "
            :is-loading-options="isLoadingCertificateAuthorities"
            :disabled="isReadOnly"
          >
            <lxc-option
              v-for="(item, index) in certificateAuthorities"
              :key="index"
              :label="item.alias"
              :value="item.alias"
              :disabled="!item.available"
            />
          </lxc-select>
        </lxc-form-item>
        <span
          :title="t('pki.form.trustChainsAliases.help')"
          class="self-center mt-3 -ml-2"
        >
          <i-lxc-help-circle />
        </span>
        <lxc-form-item
          :label="t('pki.form.login.label')"
          prop="authentication.username"
          class="col-start-1"
        >
          <lxc-input
            v-model="pkiForm.authentication!.username"
            type="text"
            :disabled="isReadOnly"
          />
        </lxc-form-item>
        <lxc-form-item
          :label="t('pki.form.password.label')"
          prop="authentication.password"
        >
          <lxc-input
            v-model="pkiForm.authentication!.password"
            type="password"
            :disabled="isReadOnly"
          />
        </lxc-form-item>
        <div class="justify-self-start flex gap-4 items-center mb-3">
          <lxc-button
            html-type="button"
            type="secondary"
            :icon="ILxcActivity"
            :title="pkiTestConnectionTitle"
            :disabled="isTestButtonDisabled"
            :is-loading="isLoadingPkiConnectivity"
            @click="testConnection"
            >{{ t("pki.form.testConnection.button") }}</lxc-button
          >
          <pki-list-test-connection-status
            :success-pki-connectivity="successPkiConnectivity"
            :error-pki-connectivity="errorPkiConnectivity"
            :is-loading="isLoadingPkiConnectivity"
          />
        </div>

        <lxc-form-item
          :label="t('pki.type.label')"
          prop="type"
          display-label
          class="col-span-2"
        >
          <div class="flex gap-4 w-full">
            <div
              v-for="(pkiType, index) of pkiTypes"
              :key="index"
              :class="getRadioWrapperClass(pkiType)"
            >
              <lxc-radio
                v-model="pkiForm.type"
                :value="pkiType"
                :label="t(`pki.type.${pkiType}`)"
                :disabled="isPkiEdition"
              />
            </div>
          </div>
        </lxc-form-item>
        <lxc-form-item
          v-if="pkiForm.type === PkiType.MICROSOFT"
          :label="t('pki.form.specificConfiguration.templateName.label')"
          prop="specificConfiguration.templateName"
        >
          <lxc-input
            v-model="pkiForm.specificConfiguration!.templateName"
            type="text"
            :disabled="isReadOnly"
          />
        </lxc-form-item>
        <lxc-form-item
          v-if="pkiForm.type === PkiType.MICROSOFT"
          :label="t('pki.form.specificConfiguration.retryAfter.label')"
          prop="specificConfiguration.retryAfter"
        >
          <lxc-input
            v-model="pkiForm.specificConfiguration!.retryAfter"
            type="number"
            :disabled="isReadOnly"
          />
        </lxc-form-item>
        <lxc-form-item
          v-if="pkiForm.type === PkiType.EST"
          :label="t('pki.form.specificConfiguration.label.urlLabel')"
          prop="specificConfiguration.urlLabel"
        >
          <lxc-input
            v-model="pkiForm.specificConfiguration!.urlLabel"
            type="text"
            :disabled="isReadOnly"
          />
        </lxc-form-item>
      </lxc-form>
    </container-component>

    <template #footer>
      <div class="flex gap-6">
        <lxc-button
          html-type="button"
          type="tertiary"
          :text="t('button.cancel')"
          :title="t('button.cancel')"
          @click="onCancelClick"
        />
        <lxc-button
          v-if="!isReadOnly"
          html-type="button"
          type="primary"
          class="grow"
          :text="
            isPkiEdition
              ? t('pki.form.validate.update')
              : t('pki.form.validate.create')
          "
          :title="
            isPkiEdition
              ? t('pki.form.validate.update')
              : t('pki.form.validate.create')
          "
          :is-loading="isLoading || isLoadingUpdate"
          :disabled="!isFormValid || !formHasChanged"
          @click="onSubmit"
        />
      </div>
    </template>
  </lxc-side-canvas>
</template>
